import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { CheckInCalendar } from '@/components/check-in/check-in-calendar'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { UsernameEditor } from '@/components/social/username-editor'
import { UsernameOnboarding } from '@/components/social/username-onboarding'
import { useAuth } from '@/lib/auth'
import { useAccountTimeZone } from '@/hooks/use-account-time-zone'
import { useAccountDayClock } from '@/hooks/use-account-day-clock'
import { useI18n } from '@/lib/i18n'
import { buildPublicPlayerProfileUrl } from '@/lib/arena'
import { useTRPC } from '@/lib/trpc'
import { getInitials } from '@/lib/utils'
import { useSocialOverview } from '@/social/social-provider'
import type { SocialOverview } from '@/social/types'
import { useQuery } from '@tanstack/react-query'
import { getNextFreeGameQuotaReset } from '@repo/sdk'
import { ExternalLink, Loader2, RefreshCw } from 'lucide-react'
import { useEffect } from 'react'
import { GamePassProfileBalance } from './game-pass-card'

export function ProfileTab() {
  const { user, signOut } = useAuth()
  const { language, t } = useI18n()
  const trpc = useTRPC()
  const accountCalendar = useAccountTimeZone()
  const socialOverviewQuery = useSocialOverview(!!user)
  const socialOverview = socialOverviewQuery.data as SocialOverview | undefined
  const username = socialOverview?.profile.username ?? null

  const { data: restrictions, refetch: refetchRestrictions, isPending: restrictionsPending } = useQuery({
    ...trpc.userRouter.getGameRestrictions.queryOptions(),
    enabled: !!user && accountCalendar.ready,
    refetchOnWindowFocus: true,
    refetchInterval: 5 * 60 * 1000,
  })
  const { isPending: emailPreferencesPending } = useQuery({
    ...trpc.emailPreferencesRouter.get.queryOptions(),
    enabled: !!user,
  })
  const accountTimeZone = restrictions?.timeZone ?? accountCalendar.timeZone
  const {
    nowMs: quotaServerNowMs,
    currentTime: currentQuotaTime,
    refresh: refreshQuotaClock,
  } = useAccountDayClock(restrictions?.serverNow)
  useEffect(() => {
    if (!user || !accountCalendar.ready)
      return
    const nextResetAtMs = restrictions?.nextResetAt
      ? Date.parse(restrictions.nextResetAt)
      : Number.NaN
    let resetTimer: number
    const scheduleReset = () => {
      const nowMs = currentQuotaTime()
      const resetAtMs = Number.isFinite(nextResetAtMs) && nextResetAtMs > nowMs
        ? nextResetAtMs
        : getNextFreeGameQuotaReset(new Date(nowMs), accountTimeZone).getTime()
      resetTimer = window.setTimeout(() => {
        refreshQuotaClock()
        void refetchRestrictions()
        scheduleReset()
      }, Math.max(0, resetAtMs - nowMs + 50))
    }
    const handleFocus = () => {
      refreshQuotaClock()
      if (Number.isFinite(nextResetAtMs) && nextResetAtMs <= currentQuotaTime())
        void refetchRestrictions()
    }
    refreshQuotaClock()
    scheduleReset()
    window.addEventListener('focus', handleFocus)
    return () => {
      window.clearTimeout(resetTimer)
      window.removeEventListener('focus', handleFocus)
    }
  }, [accountCalendar.ready, accountTimeZone, refetchRestrictions, restrictions?.nextResetAt, currentQuotaTime, refreshQuotaClock, user])
  const freeGamesUsed = restrictions?.freeGamesUsed ?? 0
  const freeGamesLimit = restrictions?.freeGamesLimit ?? 0
  const serverQuotaResetAtMs = restrictions?.nextResetAt
    ? Date.parse(restrictions.nextResetAt)
    : Number.NaN
  const quotaResetAt = Number.isFinite(serverQuotaResetAtMs)
    && serverQuotaResetAtMs > quotaServerNowMs
    ? new Date(serverQuotaResetAtMs)
    : getNextFreeGameQuotaReset(new Date(quotaServerNowMs), accountTimeZone)
  const freeGamesPercent
    = freeGamesLimit > 0
      ? Math.min(100, Math.round((freeGamesUsed / freeGamesLimit) * 100))
      : 0

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('auth.dashboard.profile')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {socialOverviewQuery.isPending ? (
          <div className="flex min-h-32 items-center justify-center rounded-lg border">
            <Loader2 className="size-5 animate-spin text-muted-foreground" />
          </div>
        ) : socialOverviewQuery.isError || !socialOverview ? (
          <div className="flex min-h-32 flex-col items-center justify-center gap-3 rounded-lg border px-4 text-center">
            <p className="text-sm text-destructive">{t('social.error')}</p>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => socialOverviewQuery.refetch()}
            >
              <RefreshCw className="size-4" />
              {t('social.retry')}
            </Button>
          </div>
        ) : username ? (
          <div className="space-y-4">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              <div className="flex min-w-0 items-center gap-4">
                <Avatar className="size-16 shrink-0">
                  <AvatarFallback className="text-lg">
                    {getInitials(username)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <p className="text-sm text-muted-foreground">
                    {t('social.username.label')}
                  </p>
                  <p className="truncate font-medium">@{username}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {socialOverview.profile.canChangeUsername
                      ? t('social.username.changeAvailable')
                      : t('social.username.permanent')}
                  </p>
                </div>
              </div>
              <Button
                asChild
                size="sm"
                variant="outline"
                className="sm:ml-auto"
              >
                <a
                  href={buildPublicPlayerProfileUrl(username, language)}
                  data-testid="view-public-player-profile"
                >
                  {t('publicPlayerProfile.viewPublicProfile')}
                  <ExternalLink className="size-4" aria-hidden="true" />
                </a>
              </Button>
            </div>
            {socialOverview.profile.canChangeUsername
              && !socialOverview.profile.isAdministrator && (
              <UsernameEditor currentUsername={username} />
            )}
          </div>
        ) : socialOverview.profile.usernameClaimEligible ? (
          <UsernameOnboarding />
        ) : (
          <div className="rounded-lg border border-dashed px-4 py-6 text-sm text-muted-foreground">
            {t('social.username.unavailable')}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium">
              {t('auth.dashboard.email')}
            </label>
            <div className="mt-1">
              <p className="text-sm text-muted-foreground">
                {user?.email || t('auth.dashboard.emailNotAvailable')}
              </p>
            </div>
          </div>
        </div>

        {accountCalendar.isError && (
          <div className="border-t pt-4" role="alert">
            <p className="text-sm text-destructive">{t('checkIn.timeZoneError')}</p>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="mt-2"
              onClick={() => { void accountCalendar.initialize().catch(() => {}) }}
            >
              {t('social.retry')}
            </Button>
          </div>
        )}
        {accountCalendar.ready && restrictions?.isLoggedIn && freeGamesLimit > 0 && (
          <div className="border-t pt-4" data-testid="daily-free-allowance">
            <div className="flex items-baseline justify-between gap-2">
              <p className="text-sm font-medium">
                {t('auth.dashboard.freeGamesThisWeek')}
              </p>
              <p className="text-sm tabular-nums text-muted-foreground">
                {t('auth.dashboard.freeGamesUsedOfLimit')
                  .replace('{used}', String(freeGamesUsed))
                  .replace('{limit}', String(freeGamesLimit))}
              </p>
            </div>
            <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-primary transition-all"
                style={{ width: `${freeGamesPercent}%` }}
              />
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              {t('checkIn.timeZone').replace('{timeZone}', accountTimeZone)}
            </p>
            {restrictions?.nextResetAt && (
              <p className="mt-2 text-xs text-muted-foreground">
                {t('auth.dashboard.freeGamesResets').replace(
                  '{date}',
                  quotaResetAt.toLocaleString(
                    language === 'tw' ? 'zh-TW' : language,
                    { dateStyle: 'medium', timeStyle: 'short', timeZone: accountTimeZone },
                  ),
                )}
              </p>
            )}
          </div>
        )}

        <CheckInCalendar scrollReady={!socialOverviewQuery.isPending && (!restrictionsPending || accountCalendar.isError) && !emailPreferencesPending} />

        <GamePassProfileBalance />

        <div className="border-t pt-4">
          <Button variant="outline" onClick={() => signOut()}>
            {t('auth.dashboard.signOut')}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
